import {
  PipelineFlow,
  useCreatePipelineFlowMutation,
  useDeletePipelineFlowMutation,
  useUpdatePipelineFlowMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UsePipelineFlowMutationProps = {}

export type UsePipelineFlowMutationResult = {
  createPipelineFlow: (pipelineFlow: Partial<PipelineFlow>, onSuccess: (() => void) | undefined) => void
  updatePipelineFlow: (pipelineFlow: Partial<PipelineFlow>, onSuccess: (() => void) | undefined) => void
  deletePipelineFlow: (pipelineId: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const usePipelineFlowMutation = (props: UsePipelineFlowMutationProps = {}): UsePipelineFlowMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createPipelineFlow, {isLoading: isCreating}] = useCreatePipelineFlowMutation()
  const [_updatePipelineFlow, {isLoading: isUpdating}] = useUpdatePipelineFlowMutation()
  const [_deletePipelineFlow, {isLoading: isDeleting}] = useDeletePipelineFlowMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "usePipelineFlowUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.pipelineflow", "Flow Volume"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("usePipelineFlowUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "usePipelineFlowUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.pipelineflow", "Flow Volume"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("usePipelineFlowUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "usePipelineFlowUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.pipelineflow", "Flow Volume"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("usePipelineFlowUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.pipelineflow", "Flow Volume") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.pipelineflow", "Flow Volume") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.pipelineflow", "Flow Volume") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((pipelineId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deletePipelineFlow({id: pipelineId}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deletePipelineFlow, handleDeleteResult])

  const createPipelineFlow = useCallback((pipelineFlow: Partial<PipelineFlow>, onSuccess: (() => void) | undefined) => {
    const promise = _createPipelineFlow(pipelineFlow).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createPipelineFlow, handleCreateResult])

  const updatePipelineFlow = useCallback((pipelineFlow: Partial<PipelineFlow>, onSuccess: (() => void) | undefined) => {
    const promise = _updatePipelineFlow(pipelineFlow).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updatePipelineFlow, handleUpdateResult])

  const deletePipelineFlow = useCallback((pipelineId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.pipelineflow", "Flow Volume"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deletepipelineflow.confirm", "Are you sure you want to delete this flow volume?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(pipelineId, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createPipelineFlow: createPipelineFlow,
    updatePipelineFlow: updatePipelineFlow,
    deletePipelineFlow: deletePipelineFlow,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
